import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, signal } from '@angular/core';
import { COMWidget } from '@/app/models/widget.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DsyRowData, DsyTableConf } from '@/app/models/dsy-table.model';
import { ExternalUrlsService } from '@/app/services/external-urls.service';
import { OffersService } from '@/app/services/offers.service';
import { OfferViewModel } from '@/app/models/com/offer.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap, timer } from 'rxjs';
import { FormatTimeRangePipe } from '@/app/pipes/format-time-range.pipe';
import { DisplayLocalePipe } from '@/app/pipes/display-locale.pipe';
import { LinkButtonComponent } from '@/app/shared/components/link-button/link-button.component';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { WidgetHeaderComponent } from '@/app/shared/components/widget-header/widget-header.component';
import { TooltipModule } from 'primeng/tooltip';
import { MixPanelEvent } from '@/app/services/mixpanel.service';
import { resizeDsyTableSortIcons } from '@/app/shared/format-styles/dsyTable-style';
import { DsyListCustomConf, DsyListElementConf, DsyTagConfig } from '@/app/models/dsy-drop-down.model';

@Component({
  selector: 'dr-flex-home-ui-com-component',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './com-offers-widget.component.html',
  styleUrl: './com-offers-widget.component.scss',
  imports: [FormatTimeRangePipe, DisplayLocalePipe, LinkButtonComponent, TranslateModule, EmptyStateComponent, WidgetHeaderComponent, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class COMOffersWidgetComponent implements AfterViewChecked {
  widget = input.required<COMWidget>();

  private readonly POLLING_INTERVAL = 15 * 60 * 1000;
  private readonly OFFER_LIMIT = 3;
  private readonly offerService = inject(OffersService);
  private readonly translate = inject(TranslateService);
  readonly mixPanelEvent = MixPanelEvent;

  readonly offers = toSignal<OfferViewModel[] | null>(
    timer(0, this.POLLING_INTERVAL).pipe(switchMap(() => this.offerService.getLatestOffers({ limit: this.OFFER_LIMIT }).pipe(catchError(() => of(null))))),
    { initialValue: null }
  );
  private readonly maxDropDownDisplayCharacters = 29;
  private readonly dsyDropDownListConfig: DsyListCustomConf = {
    minWidth: '25rem',
    maxWidth: '30rem',
    maxHeight: '13.5rem',
    status: 'default',
    border: false,
    disabled: false,
    exclusiveSubrowVisibility: true,
    reverseRender: false,
    isNavBarStyle: false,
  };
  readonly maxSelections = 3;
  //maxSelectionWarning = signal(false);
  readonly dropDownTagConfig: DsyTagConfig = {
    variant: 'secondary',
  };
  readonly selectedSiteIds = signal<Set<string> | null>(null);

  readonly siteOptions = computed<[string, string][]>(() => {
    return [...new Map((this.offers() ?? []).map((offer) => [offer.siteId, offer.siteName[0]?.label || offer.siteId])).entries()];
  });

  readonly sitesDropDownEntries = computed(() => {
    const selectedCount = this.selectedSiteIds()?.size ?? 0;
    const remaining = this.maxSelections - selectedCount;

    const message = 
      remaining <= 0
        ? this.translate.instant('FLEX_HOME.COM_WIDGET.DEMAND.MAX_SELECTED')
        :  this.translate.instant('FLEX_HOME.COM_WIDGET.DEMAND.SELECT_ANY',{remaining});

    return [
      {
        id: 'info',
        labelContent: message,
        hasRightCheckbox: false,
      } as DsyListElementConf,
      ...this.sitesDropDownList(),
    ];
  });

  readonly sitesDropDownList = computed<DsyListElementConf[]>(() => {
    const siteOptions = this.siteOptions();
    const selectedSiteIds = this.selectedSiteIds() ?? new Set(siteOptions.slice(0, this.maxSelections).map(([siteId]) => siteId));

    return siteOptions.map(
      ([siteId, siteName]) =>
        ({
          id: siteId,
          labelContent: siteName,
          density: 'wide',
          isSelected: selectedSiteIds.has(siteId),
          hasRightCheckbox: true,
          rightCheckboxSize: 'medium',
          onListElementClick: () => this.handleSiteClick(siteId),
        }) as DsyListElementConf
    );
  });

  readonly showDropDownTag = computed<boolean>(() => {
    const selectedSiteIds = this.selectedSiteIds() ?? new Set(this.siteOptions().slice(0, this.maxSelections).map(([siteId]) => siteId));
    const selectedSiteNames = this.siteOptions()
      .filter(([siteId]) => selectedSiteIds.has(siteId))
      .map(([, siteName]) => siteName);

    return this.getShowDropDownTag(selectedSiteNames);
  });

  private getShowDropDownTag(selectedSiteNames: string[]): boolean {
    if (selectedSiteNames.length === 0) return false;

    const nameSum = selectedSiteNames.reduce((sum, siteName) => sum + siteName.length, 0) + 2 * (selectedSiteNames.length - 1);
    return nameSum > this.maxDropDownDisplayCharacters;
  }
  readonly selectedOffers = computed<OfferViewModel[]>(() => {
    const selectedSiteIds = new Set(this.sitesDropDownList().filter((site) => site.isSelected).map((site) => site.id));
    return (this.offers() ?? []).filter((offer) => selectedSiteIds.has(offer.siteId));
  });

  readonly dropDownListConfig = computed<DsyListCustomConf>(() => {
    const sitesLength = this.sitesDropDownList().length;
    return {
      ...this.dsyDropDownListConfig,
      itemsToDisplay: sitesLength,
    };
  });

  readonly columnsDefinition = [
    {
      key: 'site',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.SITE',
      // This function cleanly extracts the data for a cell
      dataAccessor: (offer: OfferViewModel) => offer.siteName[0]?.label || '',
      isTime: false,
      isSortable: false,
    },
    {
      key: 'program',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.PROGRAM',
      dataAccessor: (offer: OfferViewModel) => offer.programName[0]?.label || '',
      isTime: false,
      isSortable: false,
    },
    {
      key: 'time',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.TIME_PERIOD',
      dataAccessor: (offer: OfferViewModel) => offer.offerStartDttmUtc,
      isTime: true,
      isSortable: false,
    },
    {
      key: 'value',
      header: 'FLEX_HOME.COM_WIDGET.OFFERS.COLUMNS.OFFER',
      dataAccessor: (offer: OfferViewModel) => String(offer.offerValue[0]?.value || ''),
      isTime: false,
      isSortable: true,
    },
  ];

  readonly comUrl = inject(ExternalUrlsService).com;

  // Generate the table data dynamically from the column definitions.
  readonly tableData = computed<DsyRowData[] | null>(() => {
    if (!this.offers()) return null;

    return this.selectedOffers().map((offer, index) => ({
      id: `offer-row-${index}`,
      // Create the cells for each row
      cells: Object.fromEntries(
        this.columnsDefinition.map((col) => [
          col.key,
          {
            id: `cell-${index}-${col.key}`,
            property: col.key,
            value: col.dataAccessor(offer),
          },
        ])
      ),
    }));
  });

  private handleSiteClick(clickedSiteId: string): void {
    const availableSiteIds = new Set(this.siteOptions().map(([siteId]) => siteId));
    const currentSelectedSiteIds = this.selectedSiteIds() ?? new Set(this.siteOptions().slice(0, this.maxSelections).map(([siteId]) => siteId));
    const selectedSiteIds = new Set<string>([...currentSelectedSiteIds].filter((siteId) => availableSiteIds.has(siteId)));
    
    if (selectedSiteIds.has(clickedSiteId)) {
      if (selectedSiteIds.size === 1) {
        this.selectedSiteIds.set(new Set(selectedSiteIds));
        return;
      }
      selectedSiteIds.delete(clickedSiteId);
    } else {
      if (selectedSiteIds.size >= this.maxSelections) {
        this.selectedSiteIds.set(new Set(selectedSiteIds));
        return;
      }
      selectedSiteIds.add(clickedSiteId);
    }

    this.selectedSiteIds.set(selectedSiteIds);
  }

  // Generate the table configuration dynamically from the same definitions.
  readonly tableConf = computed<DsyTableConf>(() => {
    const rows = this.tableData() ?? [];
    const rowOrder = rows.map((row) => row.id);
    const columnOrder = this.columnsDefinition.map((col) => col.key);

    return {
      styling: {
        table: {
          areRowsSeparated: true,
          density: 'default',
        },
      },
      renderOrder: {
        columns: {
          defaultOrder: columnOrder,
        },
        rows: {
          defaultOrder: rowOrder,
        },
      },
      headerRow: {
        id: 'main-header',
        cells: Object.fromEntries(
          this.columnsDefinition.map((col) => [
            col.key,
            {
              id: `header-${col.key}`,
              property: col.key,
              value: col.header,
              isSortable: col.isSortable,
            },
          ])
        ),
      },
    };
  });

  ngAfterViewChecked() {
    resizeDsyTableSortIcons();
    
  }
}
